import { fullLabel } from "../WhereItIs";
import { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Minus, Plus, Printer, Layers } from "lucide-react";
import { nextShelfPlan } from "@/lib/shelfCodes";

// ADD SHELVES — the one obvious step. It already knows which rack; nothing is re-scanned and
// nothing is typed. It runs the existing idempotent grid builder, so pressing it twice can never
// duplicate a shelf.
export const AddShelvesDialog = ({ rack, existing, onClose, onDone, onPrint }) => {
  const [count, setCount] = useState(4);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const plan = useMemo(() => nextShelfPlan(rack?.short_code || "", existing, count), [rack, existing, count]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/locations/generate-grid", {
        parent_id: rack.id, dim1_label: "Shelf", dim1_code: plan.prefix,
        dim1_count: plan.count, dim1_start: plan.start, dim1_pad: plan.pad, dim2_count: 0,
      });
      setResult(data);
      toast.success(`${rack.short_code}: ${data.created} ${data.created === 1 ? "shelf" : "shelves"} added${data.skipped ? ` (${data.skipped} already existed)` : ""}.`);
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!rack} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="add-shelves-dialog" className="rounded-xl border-slate-200 sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Add shelves to {rack?.name}</DialogTitle></DialogHeader>
        {!result ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
              <div className="font-mono text-sm font-bold" data-testid="add-shelves-rack">{rack?.short_code}</div>
              <div className="text-[11px] text-slate-500">{fullLabel(rack) || rack?.name} · {plan.existingCount} {plan.existingCount === 1 ? "shelf" : "shelves"} already on it</div>
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">How many</Label>
              <div className="flex items-center gap-2">
                <Button data-testid="add-shelves-minus" size="sm" variant="outline" className="rounded-lg h-11 w-11 border-slate-200"
                  onClick={() => setCount((c) => Math.max(1, c - 1))}><Minus className="w-4 h-4" /></Button>
                <span data-testid="add-shelves-count" className="font-display text-2xl font-bold tabular-nums w-10 text-center">{plan.count}</span>
                <Button data-testid="add-shelves-plus" size="sm" variant="outline" className="rounded-lg h-11 w-11 border-slate-200"
                  onClick={() => setCount((c) => Math.min(60, c + 1))}><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">They will be</Label>
              <div data-testid="add-shelves-preview" className="mt-1 flex flex-wrap gap-1.5">
                {plan.codes.map((c) => <span key={c} className="font-mono text-[11px] rounded-md bg-slate-100 border border-slate-200 px-2 py-1">{c}</span>)}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Carries on from the shelves this rack already has, so a code can never clash — and running it twice can never duplicate a shelf.</p>
            </div>
          </div>
        ) : (
          <div data-testid="add-shelves-result" className="space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              <span className="font-mono font-bold">{result.parent_code}</span> — {result.created} added{result.skipped ? `, ${result.skipped} already existed` : ""}
            </div>
            <p className="text-xs text-slate-500">Codes <span className="font-mono">{result.sample?.[0]}</span> to <span className="font-mono">{result.sample?.[1]}</span>. Print the stickers now, or later from the row.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-lg border-slate-200 h-11" onClick={onClose}>{result ? "Done" : "Cancel"}</Button>
          {!result ? (
            <Button data-testid="add-shelves-confirm" disabled={busy} onClick={run} className="rounded-lg h-11 bg-[#E0781A] hover:bg-[#C76714] text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Layers className="w-4 h-4 mr-1.5" />Add {plan.count} {plan.count === 1 ? "shelf" : "shelves"}</>}
            </Button>
          ) : (
            <Button data-testid="add-shelves-print" onClick={() => onPrint(result.labels, `Shelves · ${result.parent_code}`)}
              className="rounded-lg h-11 bg-[#1F1F1F] hover:bg-black text-white">
              <Printer className="w-4 h-4 mr-1.5" />Print {result.labels?.length || 0} labels
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
